import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * SmartTimeInput — Free-text time input with clinic-hour AM/PM defaults
 *
 * User types: "3:30"  → resolves to "3:30 PM" (clinic hours 6 AM – 10 PM)
 * User types: "7"     → resolves to "7:00 AM" (before noon)
 * User types: "2pm"   → resolves to "2:00 PM"
 * User types: "10:15" → resolves to "10:15 AM"
 *
 * AM/PM Resolution (when not explicitly typed):
 *   Hour 1–5   → PM (nobody books 1 AM–5 AM)
 *   Hour 6–9   → AM (morning clinic)
 *   Hour 10–11 → AM (late morning)
 *   Hour 12    → PM (noon)
 *
 * Props:
 *   value        — controlled value (raw text)
 *   onChange      — (rawText) => void
 *   resolvedTime  — the resolved "H:MM AM/PM" string (read from parent)
 *   onResolve     — (resolvedTimeStr, hour24) => void
 *   isDark        — theme flag
 *   disabled      — disabled state
 *   placeholder   — input placeholder
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Parse free-text time input into { hour24, minute, display }.
 * Returns null if the input cannot be parsed.
 */
function parseSmartTime(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const text = raw.trim().toLowerCase().replace(/\s+/g, '');

  // Explicit AM/PM patterns: "3:30pm", "3pm", "3:30 pm", "15:30"
  const explicitMatch = text.match(
    /^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)$/i
  );
  if (explicitMatch) {
    let h = parseInt(explicitMatch[1], 10);
    const m = parseInt(explicitMatch[2] || '0', 10);
    const period = explicitMatch[3].replace(/\./g, '').toLowerCase();
    if (h < 1 || h > 12 || m > 59) return null;
    if (period === 'pm' && h !== 12) h += 12;
    if (period === 'am' && h === 12) h = 0;
    return formatResult(h, m);
  }

  // 24-hour explicit: "15:30", "08:00" — only if hour >= 13
  const time24Match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (time24Match) {
    const h = parseInt(time24Match[1], 10);
    const m = parseInt(time24Match[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      if (h >= 13) {
        return formatResult(h, m); // Unambiguous 24h
      }
      // Ambiguous: apply clinic-hour defaults
      const resolved = resolveAmbiguousHour(h);
      return formatResult(resolved, m);
    }
    return null;
  }

  // Bare hour: "3", "10", "12"
  const bareHourMatch = text.match(/^(\d{1,2})$/);
  if (bareHourMatch) {
    const h = parseInt(bareHourMatch[1], 10);
    if (h < 1 || h > 12) return null;
    const resolved = resolveAmbiguousHour(h);
    return formatResult(resolved, 0);
  }

  return null;
}

/**
 * Resolve an ambiguous hour (1-12) to 24h using clinic-hour defaults.
 * Clinic hours: 6 AM – 10 PM
 *
 * Rules:
 *   1–5   → PM (13-17)  — nobody books at 1-5 AM
 *   6–9   → AM (6-9)    — morning clinic
 *   10–11 → AM (10-11)  — late morning
 *   12    → PM (12)      — noon
 */
function resolveAmbiguousHour(h) {
  if (h >= 1 && h <= 5) return h + 12;  // PM
  if (h >= 6 && h <= 11) return h;       // AM
  if (h === 12) return 12;               // Noon
  return h;
}

function formatResult(hour24, minute) {
  const h12 = hour24 % 12 || 12;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const mStr = String(minute).padStart(2, '0');
  return {
    hour24,
    minute,
    display: `${h12}:${mStr} ${period}`,
    time24: `${String(hour24).padStart(2, '0')}:${mStr}`,
  };
}

export { parseSmartTime };

export default function SmartTimeInput({
  value = '',
  onChange,
  onResolve,
  isDark = false,
  disabled = false,
  placeholder = 'e.g. 3:30, 10, 2pm',
}) {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const parsed = useMemo(() => parseSmartTime(value), [value]);

  // Notify parent of resolved time whenever it changes
  useEffect(() => {
    if (onResolve) {
      if (parsed) {
        onResolve(parsed.display, parsed.hour24, parsed.time24);
      } else {
        onResolve(null, null, null);
      }
    }
  }, [parsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value;
      // Allow only digits, colon, space, a, m, p
      const cleaned = raw.replace(/[^0-9:apmAPM.\s]/g, '').slice(0, 10);
      if (onChange) onChange(cleaned);
    },
    [onChange]
  );

  const borderColor = focused
    ? parsed
      ? isDark ? '#22c55e' : '#16a34a'
      : isDark ? '#3b82f6' : '#2563eb'
    : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)';

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          paddingRight: parsed ? '5.5rem' : '0.75rem',
          borderRadius: '0.5rem',
          border: `1px solid ${borderColor}`,
          background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
          color: isDark ? '#f5f5f7' : '#111827',
          fontSize: '0.875rem',
          outline: 'none',
          transition: 'border-color 0.15s',
          boxSizing: 'border-box',
        }}
      />

      {/* Resolved time badge */}
      {parsed && value.length > 0 && (
        <span
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#4ade80' : '#16a34a',
            background: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.08)',
            padding: '0.125rem 0.5rem',
            borderRadius: '0.375rem',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {parsed.display}
        </span>
      )}

      {/* Invalid indicator */}
      {!parsed && value.length > 2 && (
        <span
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.625rem',
            color: isDark ? '#f87171' : '#dc2626',
            pointerEvents: 'none',
          }}
        >
          Invalid time
        </span>
      )}
    </div>
  );
}
